<template>
  <tr @click="$emit('click', item)" :class="getRowClass()">
    <td class="step-cell">
      <div class="cell-content">
        <span class="step-badge" v-if="level === 1">{{ item.step || "--" }}</span>
        <span v-else>{{ item.step || "--" }}</span>
      </div>
    </td>
    <td class="channel-cell">
      <div class="cell-content">
        {{ item.approach_type || "--" }}
      </div>
    </td>
    <td class="message-cell">
      <v-tooltip bottom :disabled="!item.message" max-width="600">
        <template v-slot:activator="{ on }">
          <div class="cell-content" v-on="on">
            <span class="message-text">{{ getMessagePreview() }}</span>
          </div>
        </template>
        <div class="message-tooltip">
          <div class="message-tooltip-header">{{ item.approach_type || 'Mensagem' }}</div>
          <pre class="message-tooltip-content">{{ formatMessage() }}</pre>
        </div>
      </v-tooltip>
    </td>
    <td class="notifications-cell">
      <div class="cell-content" v-if="item.notifications">
        <div class="metric-value">{{ item.notifications | numeral("0,0") }}</div>
        <div v-if="item.approach_cost" class="metric-subtitle">
          {{ calculateTotalCost() | numeral("$0,0.00") }}
        </div>
      </div>
      <div v-else class="cell-content empty">--</div>
    </td>
    <td class="access-cell">
      <div class="cell-content" v-if="item.accessed !== undefined && item.notifications">
        <span class="metric-value">{{ item.accessed | numeral("0,0") }}</span>
        <span class="metric-percentage">
          ({{ calculatePercentage(item.accessed, item.notifications) }}%)
        </span>
      </div>
      <div v-else class="cell-content empty">--</div>
    </td>
    <td class="login-cell">
      <div class="cell-content" v-if="item.localized !== undefined && item.accessed">
        <span class="metric-value">{{ item.localized | numeral("0,0") }}</span>
        <span class="metric-percentage">
          ({{ calculatePercentage(item.localized, item.accessed) }}%)
        </span>
      </div>
      <div v-else class="cell-content empty">--</div>
    </td>
    <td class="confirmed-cell">
      <div class="cell-content" v-if="item.proposals_confirmed !== undefined && item.localized">
        <div>
          <span class="metric-value">{{ item.proposals_confirmed | numeral("0,0") }}</span>
          <span class="metric-percentage">
            ({{ calculatePercentage(item.proposals_confirmed, item.localized) }}%)
          </span>
        </div>
        <div v-if="computeCPP() !== null" class="metric-subtitle">
          CPP: {{ computeCPP() | numeral("$0,0.00") }}
        </div>
      </div>
      <div v-else class="cell-content empty">--</div>
    </td>
  </tr>
</template>

<script>
export default {
  name: "IncentiveFiringsTableRow",
  props: {
    item: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 1,
    },
  },
  methods: {
    calculatePercentage(value, total) {
      if (!total || total === 0) return "0.00";
      return ((value / total) * 100).toFixed(2);
    },
    calculateTotalCost() {
      // Para níveis 1 e 2, approach_cost já é o total calculado
      // Para nível 3, precisa calcular approach_cost × notifications
      if (this.level === 3) {
        return (this.item.approach_cost || 0) * (this.item.notifications || 0);
      }
      return this.item.approach_cost || 0;
    },
    computeCPP() {
      const confirmed = this.item.proposals_confirmed || 0;
      if (!confirmed) return null;

      // Total cost: nível 3 precisa multiplicar preço unitário × notificações
      const totalCost = this.level === 3
        ? (this.item.approach_cost || 0) * (this.item.notifications || 0)
        : (this.item.approach_cost || 0);

      return totalCost / confirmed;
    },
    getMessagePreview() {
      if (!this.item.message) return "--";
      
      try {
        const parsed = JSON.parse(this.item.message);
        
        // PUSH - extrair body ou title
        if (parsed.body) {
          return parsed.body.substring(0, 50) + (parsed.body.length > 50 ? "..." : "");
        }
        
        // WHATSAPP - extrair message
        if (parsed.message) {
          const cleanMessage = parsed.message.replace(/\\n/g, " ").trim();
          return cleanMessage.substring(0, 50) + (cleanMessage.length > 50 ? "..." : "");
        }
        
        return this.item.message.substring(0, 50) + (this.item.message.length > 50 ? "..." : "");
      } catch (e) {
        // SMS - texto simples
        return this.item.message.substring(0, 50) + (this.item.message.length > 50 ? "..." : "");
      }
    },
    formatMessage() {
      if (!this.item.message) return "Sem mensagem";
      
      try {
        const parsed = JSON.parse(this.item.message);
        
        // PUSH
        if (parsed.title || parsed.body) {
          let formatted = "";
          if (parsed.title) formatted += `Título:\n${parsed.title}\n\n`;
          if (parsed.body) formatted += `Corpo:\n${parsed.body}`;
          return formatted;
        }
        
        // WHATSAPP
        if (parsed.message) {
          let formatted = parsed.message.replace(/\\n/g, "\n");
          if (parsed.key) formatted += `\n\nTemplate: ${parsed.key}`;
          return formatted;
        }
        
        // JSON genérico
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // SMS - texto simples
        return this.item.message;
      }
    },
    getRowClass() {
      const step = this.item.step;
      if (!step) return `level-${this.level}`;
      
      const baseClass = `level-${this.level}`;
      const stepLower = step.toString().toLowerCase().trim();
      
      // Cores por step (case-insensitive)
      if (stepLower.includes("landing") || stepLower.includes("acessada")) {
        return `${baseClass} step-landing`;
      } else if (stepLower.includes("propostas") && stepLower.includes("sincronizada")) {
        return `${baseClass} step-propostas`;
      } else if (stepLower.includes("proposta") && stepLower.includes("selecionada")) {
        return `${baseClass} step-selecionada`;
      } else if (stepLower.includes("login")) {
        return `${baseClass} step-login`;
      } else if (stepLower.includes("link") && stepLower.includes("acessado")) {
        return `${baseClass} step-link`;
      }
      
      return baseClass;
    },
  },
};
</script>

<style lang="scss" scoped>
tr {
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
}

.cell-content {
  padding: 8px 4px;
  
  &.empty {
    color: #999;
    font-style: italic;
  }
}

.step-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 0.85em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.message-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.message-tooltip {
  text-align: left;
}

.message-tooltip-header {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
}

.message-tooltip-content {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  color: #fff;
  max-width: 550px;
}

.metric-value {
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 2px;
}

.metric-subtitle {
  font-size: 0.85em;
  opacity: 0.75;
}

.metric-percentage {
  font-size: 0.9em;
  font-weight: 600;
  color: #1976d2;
  
  &.success {
    color: #2e7d32;
  }
}

/* ===== NÍVEL 1 - Cores base pastel com texto escuro ===== */
.level-1 {
  font-weight: 600;
  cursor: pointer;
  border-left-width: 5px;
  
  &.step-landing {
    background-color: #c8e6c9; /* Verde pastel */
    border-left-color: #4caf50;
    color: #1b5e20;
  }
  
  &.step-propostas {
    background-color: #e1bee7; /* Roxo pastel */
    border-left-color: #9c27b0;
    color: #4a148c;
  }
  
  &.step-selecionada {
    background-color: #bbdefb; /* Azul pastel */
    border-left-color: #2196f3;
    color: #0d47a1;
  }
  
  &.step-login {
    background-color: #fff8e1; /* Amarelo amber claro */
    border-left-color: #ffa726;
    color: #e65100;
  }
  
  &.step-link {
    background-color: #dcedc8; /* Verde musgo pastel */
    border-left-color: #689f38;
    color: #33691e;
  }
  
  .metric-percentage {
    color: inherit;
    opacity: 0.8;
  }
}

/* ===== NÍVEL 2 - Tom mais claro ===== */
.level-2 {
  font-weight: 500;
  cursor: pointer;
  border-left-width: 4px;
  
  &.step-landing {
    background-color: #e8f5e9;
    border-left-color: #66bb6a;
    color: #2e7d32;
  }
  
  &.step-propostas {
    background-color: #f3e5f5;
    border-left-color: #ab47bc;
    color: #6a1b9a;
  }
  
  &.step-selecionada {
    background-color: #e3f2fd;
    border-left-color: #42a5f5;
    color: #1565c0;
  }
  
  &.step-login {
    background-color: #fffbf0;
    border-left-color: #ffb74d;
    color: #ef6c00;
  }
  
  &.step-link {
    background-color: #f1f8e9;
    border-left-color: #7cb342;
    color: #558b2f;
  }
  
  .metric-percentage {
    color: inherit;
    opacity: 0.75;
  }
}

/* ===== NÍVEL 3 - Tom mais claro ainda ===== */
.level-3 {
  font-weight: 400;
  border-left-width: 3px;
  
  &.step-landing {
    background-color: #f1f8f4;
    border-left-color: #81c784;
    color: #388e3c;
  }
  
  &.step-propostas {
    background-color: #faf5fb;
    border-left-color: #ba68c8;
    color: #7b1fa2;
  }
  
  &.step-selecionada {
    background-color: #f5f9fd;
    border-left-color: #64b5f6;
    color: #1976d2;
  }
  
  &.step-login {
    background-color: #fffef8;
    border-left-color: #ffcc80;
    color: #f57c00;
  }
  
  &.step-link {
    background-color: #f7faf5;
    border-left-color: #8bc34a;
    color: #689f38;
  }
  
  .metric-percentage {
    color: inherit;
    opacity: 0.7;
  }
}

/* Remover hover effect do Vuetify */
tr:hover {
  background-color: inherit !important;
}
</style>
